import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import setIn from 'lodash/fp/set';
import config from '../services/config-service';
import type { ApolloGqlTalentForTalentBoard, TalentBoardComment } from '@bloomays-lib/types.shared';

// Durée de validité du cache en millisecondes (1 minute = 60000ms)
const CACHE_TTL_MS = 60000; // 1 minute

const { release: CURRENT_RELEASE } = config();

export type JobTalentsCache = {
  talents: Record<string, ApolloGqlTalentForTalentBoard>;
  lastFetchedAt: number;
  release: string;
};

export type TalentsForTalentBoardState = {
  talentsByJobId: Record<string, JobTalentsCache>;
  loading: boolean;
  error?: Error;
  setTalents: (jobId: string, talents: Record<string, ApolloGqlTalentForTalentBoard>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | undefined) => void;
  getTalents: (jobId: string) => Record<string, ApolloGqlTalentForTalentBoard> | null;
  updateTalent: (jobId: string, talent: ApolloGqlTalentForTalentBoard) => void;
  updateTalentMarkedAsSeen: (
    jobId: string,
    talentId: string,
    markedAsSeen: boolean,
    dateMarkedAsSeen?: Date | null,
  ) => void;
  upsertTalentComments: (jobId: string, talentId: string, comment: TalentBoardComment) => void;
  deleteTalentComments: (jobId: string, talentId: string, commentId: string) => void;
  mustRefreshTalentsForTalentBoard: (jobId: string) => boolean;
  reset: () => void;
};

export const useTalentsForTalentBoardStore = create<TalentsForTalentBoardState>()(
  persist(
    (set, get) => ({
      talentsByJobId: {},
      loading: false,
      error: undefined,

      setTalents: (jobId: string, talents: Record<string, ApolloGqlTalentForTalentBoard>) =>
        set((state) => ({
          talentsByJobId: {
            ...state.talentsByJobId,
            [jobId]: {
              talents,
              lastFetchedAt: Date.now(),
              release: CURRENT_RELEASE,
            },
          },
          error: undefined,
        })),

      setLoading: (loading: boolean) => set({ loading }),

      setError: (error: Error | undefined) => set({ error, loading: false }),

      getTalents: (jobId: string) => {
        const { talentsByJobId } = get();
        const jobCache = talentsByJobId[jobId];

        if (!jobCache) {
          return null;
        }

        // Invalide les données persistées provenant d'une autre release
        if (jobCache.release !== CURRENT_RELEASE) {
          return null;
        }

        return jobCache.talents;
      },

      updateTalent: (jobId: string, talent: ApolloGqlTalentForTalentBoard) =>
        set((state) => {
          const talentsCache = state.talentsByJobId[jobId];
          if (!talentsCache) return state;

          return {
            talentsByJobId: {
              ...state.talentsByJobId,
              [jobId]: {
                ...talentsCache,
                talents: {
                  ...talentsCache.talents,
                  [talent.id]: talent,
                },
              },
            },
          };
        }),

      updateTalentMarkedAsSeen: (
        jobId: string,
        talentId: string,
        markedAsSeen: boolean,
        dateMarkedAsSeen?: Date | null,
      ) =>
        set((state) => {
          const talentsCache = state.talentsByJobId[jobId];
          if (!talentsCache || !talentsCache.talents[talentId]) return state;

          const updatedState = setIn(
            ['talentsByJobId', jobId, 'talents', talentId, 'markedAsSeen'],
            markedAsSeen,
          )(state);
          return setIn(
            ['talentsByJobId', jobId, 'talents', talentId, 'dateMarkedAsSeen'],
            dateMarkedAsSeen ?? null,
          )(updatedState);
        }),

      upsertTalentComments: (jobId: string, talentId: string, comment: TalentBoardComment) =>
        set((state) => {
          const talentsCache = state.talentsByJobId[jobId];
          if (!talentsCache || !talentsCache.talents[talentId]) return state;

          const talent = talentsCache.talents[talentId];
          const existingComments = talent.comments || [];
          const commentIndex = existingComments.findIndex((c) => c.id === comment.id);

          let updatedComments: TalentBoardComment[];
          if (commentIndex >= 0) {
            // Mise à jour d'un commentaire existant
            updatedComments = [...existingComments];
            updatedComments[commentIndex] = comment;
          } else {
            // Ajout d'un nouveau commentaire
            updatedComments = [...existingComments, comment];
          }

          return setIn(['talentsByJobId', jobId, 'talents', talentId, 'comments'], updatedComments)(state);
        }),

      deleteTalentComments: (jobId: string, talentId: string, commentId: string) =>
        set((state) => {
          const talentsCache = state.talentsByJobId[jobId];
          if (!talentsCache || !talentsCache.talents[talentId]) return state;

          const talent = talentsCache.talents[talentId];
          const existingComments = talent.comments || [];
          const updatedComments = existingComments.filter((c) => c.id !== commentId);

          return setIn(['talentsByJobId', jobId, 'talents', talentId, 'comments'], updatedComments)(state);
        }),

      // Vérifie si le cache est encore valide pour un jobId donné
      mustRefreshTalentsForTalentBoard: (jobId: string) => {
        const { talentsByJobId } = get();
        const jobCache = talentsByJobId[jobId];

        // Pas de cache pour ce jobId = cache invalide
        if (!jobCache) {
          return true;
        }

        // Release différente = cache invalide
        if (jobCache.release !== CURRENT_RELEASE) {
          return true;
        }

        // Vérifie si la durée de validité du localStorage n'a pas expiré
        const now = Date.now();
        const elapsed = now - jobCache.lastFetchedAt;
        const isInvalid = elapsed > CACHE_TTL_MS;
        return isInvalid;
      },

      reset: () =>
        set({
          talentsByJobId: {},
          loading: false,
          error: undefined,
        }),
    }),
    {
      name: 'bloomays-talentsfortalentboard-cache',
      partialize: (state) => ({
        talentsByJobId: state.talentsByJobId,
      }),
    },
  ),
);

export default useTalentsForTalentBoardStore;
